/**
 * Standalone Segment User Manager
 * Manages user data and event tracking without Alpine.js dependency
 *
 * Identify-first guarantee: when a userId is known, analytics.identify()
 * always fires before analytics.track()/page() within the same call,
 * eliminating race conditions between identify and other events.
 */

import { checkPurchaseHistoryIfNeeded } from '@/scripts/core/purchase-checks';

const SegmentUser = {
  traits: {},
  customerTraits: {},
  email: '',
  isInitialized: false,
  _hasIdentified: false,
  pendingEvents: [],
  maxRetries: 50,
  retryDelay: 50,
  productsData: [],

  /**
   * Initialize the user manager
   * Called when analytics SDK is ready
   */
  init() {
    this.isInitialized = true;

    // Pick up customer data set from Liquid before this module loaded
    if (window.__pvolveCustomer) {
      const { email, traits } = window.__pvolveCustomer;
      if (email) {
        this.setEmail(email);
        this.customerTraits = { ...this.customerTraits, ...traits };
      }
      delete window.__pvolveCustomer;
    }

    console.log('[SegmentUser] Initialized', this.email ? `(user: ${this.email})` : '(anonymous)');
    this.processPendingEvents();
    // Start IP address fetch in background
    this.setIpAddress();
  },

  /**
   * Store customer data from Shopify Liquid (called before analytics loads)
   * This avoids needing a separate retry loop for identify.
   * @param {String} email - Customer email
   * @param {Object} traits - Customer traits from Shopify
   */
  setCustomerData(email, traits = {}) {
    if (email) {
      this.setEmail(email);
      this.customerTraits = { ...this.customerTraits, ...traits };
    }
  },

  /**
   * Get cookie value by name
   * @param {String} name - Cookie name
   * @returns {String} Cookie value
   */
  getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return '';
  },

  /**
   * Segment stores IDs in localStorage as JSON values. Reading them as raw
   * strings can turn JSON null into a literal "null" userId; repeated
   * identify(null-as-string) calls cause Segment to recursively quote the value
   * until the payload exceeds Segment's size limit.
   * @param {String|null} value - Raw localStorage value
   * @returns {String} Safe plain string ID or empty string
   */
  normalizeSegmentStoredId(value) {
    if (value === null || value === undefined) return '';
    let current = String(value).trim();

    for (let i = 0; i < 32; i += 1) {
      if (!current) return '';
      const lower = current.toLowerCase();
      if (lower === 'null' || lower === 'undefined') return '';

      try {
        const parsed = JSON.parse(current);
        if (parsed === null || parsed === undefined) return '';
        if (typeof parsed !== 'string') return '';
        if (parsed === current) break;
        current = parsed.trim();
      } catch (_) {
        break;
      }
    }

    if (!current || current.length > 512) return '';
    if (/^["'\\]+$/.test(current)) return '';
    return current;
  },

  getSegmentStoredId(key) {
    try {
      const raw = window.localStorage.getItem(key);
      const normalized = this.normalizeSegmentStoredId(raw);
      if (!normalized && raw) {
        window.localStorage.removeItem(key);
      }
      return normalized;
    } catch (_) {
      return '';
    }
  },

  /**
   * Get user ID from email or localStorage
   * @returns {String} User ID (email)
   */
  async getUserId() {
    try {
      let userId = this.email;
      if (!userId) {
        userId = this.getSegmentStoredId('ajs_user_id');
      }
      return userId;
    } catch (e) {
      console.error('[SegmentUser] Error getting user ID:', e);
      return '';
    }
  },

  getUserIdSync() {
    try {
      return this.email || this.getSegmentStoredId('ajs_user_id');
    } catch (e) {
      return this.email || '';
    }
  },

  /**
   * Fetch and cache IP address from Lightbox CDN
   * @returns {String} IP address
   */
  async setIpAddress() {
    // Check cache first
    const cachedIp = window.sessionStorage.getItem('ipAddress');
    if (cachedIp) {
      try {
        return JSON.parse(cachedIp);
      } catch (e) {}
    }
    // Fetch IP in background
    try {
      const response = await fetch('https://api.lightboxcdn.com/');
      const data = await response.json();
      window.sessionStorage.setItem('ipAddress', JSON.stringify(data.geo.ip));
      return data.geo.ip;
    } catch (e) {
      return '';
    }
  },

  /**
   * Get IP address synchronously (non-blocking)
   * @returns {String} Cached IP or empty string
   */
  getIpAddressSync() {
    const cachedIp = window.sessionStorage.getItem('ipAddress');
    if (cachedIp) {
      try {
        return JSON.parse(cachedIp);
      } catch (e) {}
    }
    // Start background fetch but don't wait
    this.setIpAddress();
    return '';
  },

  /**
   * Build Segment consent context from __pvolveConsent.categories.
   * Reads live at send time so mid-session denial propagates.
   * @returns {Object} Segment consent categoryPreferences object
   */
  getConsentMetadata(metadata = {}) {
    const live = (window.__pvolveConsent && typeof window.__pvolveConsent === 'object') ? window.__pvolveConsent : {};
    const source = { ...live, ...metadata };
    return {
      policy: source.policy || 'none',
      policyRegion: source.policyRegion || 'unknown',
      consentVersion: source.consentVersion || source.version || 'unknown',
    };
  },

  getConsentContext(categories, metadata = {}) {
    try {
      if (typeof window.pvolveSegmentConsentContext === 'function') {
        return window.pvolveSegmentConsentContext(categories, metadata);
      }
      const cats = categories || (window.__pvolveConsent && window.__pvolveConsent.categories) || {};
      const consentMetadata = this.getConsentMetadata(metadata);
      let dataSharing = typeof cats.dataSharing === 'boolean'
        ? cats.dataSharing
        : consentMetadata.policy !== 'gdpr' || consentMetadata.policyRegion === 'CA';
      try {
        const shopifyConsent = window.Shopify && window.Shopify.customerPrivacy &&
          typeof window.Shopify.customerPrivacy.currentVisitorConsent === 'function'
          ? window.Shopify.customerPrivacy.currentVisitorConsent()
          : null;
        if (typeof cats.dataSharing !== 'boolean' && shopifyConsent) {
          if (typeof shopifyConsent.sale_of_data === 'boolean') {
            dataSharing = shopifyConsent.sale_of_data;
          } else if (typeof shopifyConsent.sale_of_data === 'string' && shopifyConsent.sale_of_data !== '') {
            dataSharing = /^(yes|true|1|allowed)$/i.test(shopifyConsent.sale_of_data);
          }
        }
      } catch (e) {}
      return {
        consent: {
          categoryPreferences: {
            Functional: !!cats.functional,
            Analytics: !!cats.performance,
            Advertising: !!cats.marketing,
            DataSharing: dataSharing,
          },
          policy: consentMetadata.policy,
          policyRegion: consentMetadata.policyRegion,
          consentVersion: consentMetadata.consentVersion,
        },
      };
    } catch (_) {
      return {
        consent: {
          categoryPreferences: {},
          policy: 'none',
          policyRegion: 'unknown',
          consentVersion: 'unknown',
        },
      };
    }
  },

  /**
   * Send an identify + track batch and require an HTTP success response.
   * Used for critical user-facing submissions that must not report success
   * when Segment is blocked or rejects the request.
   * @param {Object|null} traits - User traits for identify
   * @param {Object} eventData - Track event with eventName
   * @returns {Promise<boolean>} True only after Segment accepts the batch
   */
  async sendAcknowledgedSegmentEvent(traits, eventData) {
    if (!eventData || !eventData.eventName) throw new Error('A Segment event is required.');
    const writeKey = window._segmentWriteKey || (typeof analytics === 'object' && analytics._writeKey);
    if (!writeKey) throw new Error('Segment write key is unavailable. Please try again.');

    const userId = this.getUserIdSync();
    const anonymousId = this.getSegmentStoredId('ajs_anonymous_id');
    const ip = this.getIpAddressSync();
    const fbpCookie = this.getCookie('_fbp') || '';
    const fbcCookie = this.getCookie('_fbc') || '';
    const consentContext = eventData.__consentCategories
      ? this.getConsentContext(eventData.__consentCategories, eventData.__consentMetadata)
      : this.getConsentContext();
    const pageContext = {
      path: window.location.pathname,
      url: window.location.href,
      search: window.location.search,
      title: document.title,
      referrer: document.referrer,
    };
    const context = { page: pageContext, ...consentContext };
    const { eventName, __consentCategories, __consentMetadata, ...properties } = eventData;
    const batch = [];

    if (traits !== null && traits !== undefined && userId) {
      const mergedTraits = {
        ...this.customerTraits,
        ...traits,
        sent_from: 'Pvolve Web Script',
        fbp: fbpCookie,
        fbc: fbcCookie,
        anonymousId: anonymousId,
        ip: ip,
      };
      delete mergedTraits.email;
      batch.push({
        type: 'identify',
        userId: userId,
        anonymousId: anonymousId || undefined,
        traits: mergedTraits,
        context: context,
        timestamp: new Date().toISOString(),
      });
    }

    batch.push({
      type: 'track',
      event: eventName,
      userId: userId || undefined,
      anonymousId: anonymousId || undefined,
      properties: properties,
      context: context,
      timestamp: new Date().toISOString(),
    });

    const response = await fetch('https://api.segment.io/v1/batch', {
      method: 'POST',
      headers: {
        'Authorization': 'Basic ' + btoa(writeKey + ':'),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ batch: batch }),
    });
    if (!response.ok) {
      throw new Error('Segment rejected the privacy request with status ' + response.status + '.');
    }
    return true;
  },

  /**
   * Send a Segment event with identify-first guarantee.
   *
   * When `traits` is non-null (even `{}`) and there is a known userId,
   * analytics.identify() fires BEFORE analytics.track()/page().
   * Stored customerTraits are merged into every identify call.
   *
   * @param {Object|null} traits - User traits to identify (pass {} for auto-identify, null to skip)
   * @param {Object|null} eventData - Event data with eventName and properties
   */
  async sendSegmentEvent(traits, eventData) {
    // Snapshot page context synchronously before any async work,
    // so navigation between awaits cannot corrupt context.page
    const pageContext = {
      path: window.location.pathname,
      url: window.location.href,
      search: window.location.search,
      title: document.title,
      referrer: document.referrer,
    };

    if (typeof analytics === 'object') {
      try {
        // Get Facebook pixel cookies
        const fbpCookie = this.getCookie('_fbp') || '';
        const fbcCookie = this.getCookie('_fbc') || '';
        const segmentAnonId = this.getSegmentStoredId('ajs_anonymous_id');
        const isPageView = eventData != null && eventData.is_page_view;
        const isDoNotSellFormEvent = eventData != null && (
          eventData.eventName === 'Form Submitted' &&
          eventData.form_name === 'Do Not Sell My Info'
        );
        const isConsentEvent = eventData != null && (
          /^Cookie Banner /.test(eventData.eventName || '') ||
          isDoNotSellFormEvent
        );
        const explicitConsentContext = eventData && eventData.__consentCategories
          ? this.getConsentContext(eventData.__consentCategories, eventData.__consentMetadata)
          : null;

        if (isConsentEvent) {
          const userId = this.getUserIdSync();
          const ip = this.getIpAddressSync();
          const consentCtx = explicitConsentContext || this.getConsentContext();
          if (traits !== null && traits !== undefined && userId) {
            const mergedTraits = {
              ...this.customerTraits,
              ...traits,
              sent_from: 'Pvolve Web Script',
              fbp: fbpCookie,
              fbc: fbcCookie,
              anonymousId: segmentAnonId,
              ip: ip,
            };
            delete mergedTraits.email;
            analytics.identify(userId, mergedTraits, { context: consentCtx });
            this._hasIdentified = true;
          }
          const { eventName, __consentCategories, __consentMetadata, ...properties } = eventData;
          const enrichedProperties = isDoNotSellFormEvent ? properties : {
            ...properties,
            sent_from: 'Pvolve Web Script',
            fbp: fbpCookie,
            fbc: fbcCookie,
            userId: userId,
            anonymousId: segmentAnonId,
            ip: ip,
            email: properties.email || userId,
          };
          analytics.track(eventName, enrichedProperties, { context: { page: pageContext, ...consentCtx } });
          console.log('[SegmentUser] Event tracked:', eventName);
          return;
        }

        const userId = await this.getUserId();

        // Get IP address (non-blocking for page views)
        const ip = isPageView ? this.getIpAddressSync() : await this.setIpAddress();

        // Identify-first: when traits is non-null and we have a userId,
        // always call identify before any track/page (Skyline pattern)
        if (traits !== null && traits !== undefined && userId) {
          const mergedTraits = {
            ...this.customerTraits,
            ...traits,
            sent_from: 'Pvolve Web Script',
            fbp: fbpCookie,
            fbc: fbcCookie,
            anonymousId: segmentAnonId,
            ip: ip,
          };
          // Email goes as userId, not as a trait
          delete mergedTraits.email;
          const consentContext = this.getConsentContext();
          analytics.identify(userId, mergedTraits, { context: consentContext });
          this._hasIdentified = true;
        }

        // Track event if data provided
        if (eventData && eventData.eventName) {
          const { eventName, __consentCategories, __consentMetadata, ...properties } = eventData;
          const enrichedProperties = {
            ...properties,
            sent_from: 'Pvolve Web Script',
            fbp: fbpCookie,
            fbc: fbcCookie,
            userId: userId,
            anonymousId: segmentAnonId,
            ip: ip,
            email: userId,
          };

          // Pass snapshotted page context so the SDK uses the correct page
          // even if window.location changed during the async gap
          const consentCtx = explicitConsentContext || this.getConsentContext();
          const contextOverride = { context: { page: pageContext, ...consentCtx } };

          // Use analytics.page() for page views, analytics.track() for other events
          if (isPageView) {
            delete enrichedProperties.is_page_view;
            analytics.page(eventName, enrichedProperties, contextOverride);
          } else {
            analytics.track(eventName, enrichedProperties, contextOverride);
          }
          console.log('[SegmentUser] Event tracked:', eventName);
        }
      } catch (err) {
        console.error('[SegmentUser] Error sending event:', err);
      }
    } else {
      // Queue event if analytics not ready
      this.pendingEvents.push({ traits, eventData });
      console.warn('[SegmentUser] Analytics not ready, event queued:', eventData?.eventName);
    }
  },

  /**
   * Send a product clicked event
   * @param {String} productHandle - The product handle/slug
   */
  sendProductClickedEvent(productHandle) {
    if (!productHandle) return;

    // Defensive check: ensure productsData is an array
    if (!Array.isArray(this.productsData)) {
      this.productsData = [];
    }

    const foundProduct = this.productsData.find((product) => product.handle === productHandle);

    if (foundProduct) {
      // Send enriched product data if found
      const formattedPrice = foundProduct.price / 100;
      this.sendSegmentEvent(null, {
        eventName: 'Product Clicked',
        sent_from: 'Pvolve Web Script',
        brand: foundProduct.vendor,
        category: foundProduct.product_type,
        url: window.location.href + '/products/' + foundProduct.handle,
        product_id: foundProduct.id,
        sku: foundProduct.variants[0]?.sku || '',
        name: foundProduct.title,
        price: formattedPrice,
        variant: foundProduct.variants[0]?.title || '',
        shopify_product_id: foundProduct.id,
        shopify_variant_id: foundProduct.variants[0]?.id || '',
        currency: foundProduct.currency || 'USD',
        image_url: foundProduct.featured_image,
        tags: foundProduct.tags,
        compare_at_price: foundProduct.compare_at_price,
      });
    } else {
      // Send basic event if product data not available
      this.sendSegmentEvent(null, {
        eventName: 'Product Clicked',
        product_handle: productHandle,
        sent_from: 'Pvolve Web Script',
      });
    }
  },

  /**
   * Set products data for enriched product click events
   * @param {Array} products - Array of product objects
   */
  setProductsData(products) {
    if (Array.isArray(products)) {
      this.productsData = products;
    } else if (typeof products === 'string') {
      try {
        this.productsData = JSON.parse(products);
      } catch (e) {
        this.productsData = [];
      }
    } else {
      this.productsData = [];
    }
  },

  /**
   * Set user email
   * @param {String} email - User email
   */
  setEmail(email) {
    if (email) {
      this.email = email.toLowerCase();
      this.traits.email = this.email;
      checkPurchaseHistoryIfNeeded(this.email);
    }
  },

  /**
   * Identify user with email and traits.
   * Stores traits and fires identify immediately if analytics is ready.
   * @param {String} email - User email
   * @param {Object} additionalTraits - Additional user traits
   */
  identifyUser(email, additionalTraits = {}) {
    if (!email) return;

    this.setEmail(email);
    this.customerTraits = { ...this.customerTraits, ...additionalTraits };

    // Fire identify immediately (sendSegmentEvent handles queuing if SDK not ready)
    this.sendSegmentEvent(additionalTraits, null);
    console.log('[SegmentUser] User identified:', email);
  },

  /**
   * Get stored user email
   * @returns {String} User email
   */
  getEmail() {
    return this.email;
  },

  /**
   * Get stored user traits
   * @returns {Object} User traits
   */
  getTraits() {
    return this.traits;
  },

  /**
   * Process all pending events once analytics is ready
   */
  async processPendingEvents() {
    if (this.pendingEvents.length === 0) return;

    console.log(`[SegmentUser] Processing ${this.pendingEvents.length} pending events`);

    for (const { traits, eventData } of this.pendingEvents) {
      await this.sendSegmentEvent(traits, eventData);
    }

    this.pendingEvents = [];
  },

  /**
   * Check if analytics SDK is loaded and initialize if ready
   * Uses exponential backoff for retries
   * @param {Number} retryCount - Current retry attempt
   */
  checkAndInitialize(retryCount = 0) {
    if (typeof analytics === 'object') {
      this.init();
    } else if (retryCount < this.maxRetries) {
      const delay = Math.min(this.retryDelay + (retryCount * 25), 200);
      setTimeout(() => this.checkAndInitialize(retryCount + 1), delay);
    } else {
      console.error('[SegmentUser] Analytics SDK failed to load after maximum retries');
    }
  },
};

// Start checking for analytics availability
SegmentUser.checkAndInitialize();

// Make globally accessible
window.SegmentUser = SegmentUser;

// Export for use in other modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = SegmentUser;
}
